import React from 'react';

import cx from 'classnames';

import Button from 'components/Button';
import Modal from 'components/Modal/Modal';

import { useBnplPromo } from 'hooks/bnpl/useBnplPromo';
import { TServiceOptionsModalProps } from 'hooks/page/services/useServicesPage';
import { useGGTheme } from 'hooks/theme/useGGTheme';

import { BuyNowPayLaterPromo } from 'routes/invoices/components/BuyNowPayLaterPromo/BuyNowPayLaterPromo';

import {
  getDepositText,
  getDisplayedPrice,
  getDurationText,
  getServiceOrOptionBnplUnitPrice,
} from 'utils/serviceSelectionUtils';

import s from './ServiceOptionsModal.module.css';

export const ServiceOptionsModal = ({
  service,
  onClose,
  onSelect,
}: TServiceOptionsModalProps) => {
  const [selected, setSelected] = React.useState<string>();

  const { theme } = useGGTheme();
  const promoTextColor = theme?.palette.textTertiary as string;
  const promoBackgroundColor = '#fff';
  const bnplPromo = useBnplPromo();

  const handleClose = () => {
    setSelected(undefined);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (service && selected) {
      onSelect(service.guid, selected, service.booking_order);
    }

    handleClose();
  };

  return !!service ? (
    <Modal
      id="service-option-modal"
      className={s.modalContainer}
      titleClassName={s.title}
      title={service.name}
      onClose={handleClose}
      aria-label="service-option-modal"
      bottomDrawer
    >
      {service.description && (
        <p className={s.modalDescription}>{service.description}</p>
      )}
      <div className={s.modalContent}>
        <form onSubmit={handleSubmit}>
          {service?.options?.map((option) => {
            const priceText = getDisplayedPrice(option);
            const durationText = getDurationText(option);
            const depositText = getDepositText(option);
            const bnplEligibleUnitPrice = bnplPromo.isEnabled
              ? getServiceOrOptionBnplUnitPrice(option)
              : 0;

            return (
              <div
                key={option.guid}
                className={cx(s.item, {
                  [s.selected]: selected === option.guid,
                })}
              >
                <div key={option.guid} className={s.optionContainer}>
                  <input
                    type="radio"
                    value={option.guid}
                    id={option.guid}
                    name="service-option"
                    checked={selected === option.guid}
                    onChange={() => {
                      setSelected(option.guid);
                    }}
                    data-testid={`option-${option.guid}`}
                  />
                  <label
                    htmlFor={option.guid}
                    className={s.optionLabel}
                    key={option.guid}
                  >
                    <div className={s.optionDetail}>
                      <span className={s.optionName}>{option.name}</span>
                      {durationText && (
                        <span className={s.optionDescription}>
                          {durationText}
                        </span>
                      )}
                      {depositText && (
                        <span className={s.optionDeposit}>{depositText}</span>
                      )}
                    </div>
                    {priceText && (
                      <span className={s.optionPrice}>{priceText}</span>
                    )}
                  </label>
                </div>
                {bnplEligibleUnitPrice ? (
                  <div className={s.bnplEligible}>
                    <BuyNowPayLaterPromo
                      unitAmount={bnplEligibleUnitPrice}
                      textAlign="right"
                      textColor={promoTextColor}
                      backgroundColor={promoBackgroundColor}
                      paymentMethodTypes={bnplPromo.bnplCapabilities}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
          <div className={s.actionWrapper}>
            <Button
              popovertarget="service-option-modal"
              popovertargetaction="hide"
              className={s.action}
              disabled={!selected}
              data-testid="action-button"
            >
              Select
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  ) : null;
};
